import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, Calendar, ArrowRight, FileText, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BIBLE_BOOKS = {
  ancien_testament: [
    { id: "genese", name: "Genèse", chapters: 50 }, { id: "exode", name: "Exode", chapters: 40 }, 
    { id: "levitique", name: "Lévitique", chapters: 27 }, { id: "nombres", name: "Nombres", chapters: 36 }, 
    { id: "deuteronome", name: "Deutéronome", chapters: 34 }, { id: "josue", name: "Josué", chapters: 24 },
    { id: "juges", name: "Juges", chapters: 21 }, { id: "ruth", name: "Ruth", chapters: 4 }, 
    { id: "1samuel", name: "1 Samuel", chapters: 31 }, { id: "2samuel", name: "2 Samuel", chapters: 24 }, 
    { id: "1rois", name: "1 Rois", chapters: 22 }, { id: "2rois", name: "2 Rois", chapters: 25 },
    { id: "1chroniques", name: "1 Chroniques", chapters: 29 }, { id: "2chroniques", name: "2 Chroniques", chapters: 36 },
    { id: "esdras", name: "Esdras", chapters: 10 }, { id: "nehemie", name: "Néhémie", chapters: 13 }, 
    { id: "tobie", name: "Tobie", chapters: 14 }, { id: "judith", name: "Judith", chapters: 16 }, 
    { id: "esther", name: "Esther", chapters: 10 }, { id: "1maccabees", name: "1 Maccabées", chapters: 16 },
    { id: "2maccabees", name: "2 Maccabées", chapters: 15 }, { id: "job", name: "Job", chapters: 42 }, 
    { id: "psaumes", name: "Psaumes", chapters: 150 }, { id: "proverbes", name: "Proverbes", chapters: 31 }, 
    { id: "ecclesiaste", name: "Ecclésiaste", chapters: 12 }, { id: "cantique", name: "Cantique", chapters: 8 }, 
    { id: "sagesse", name: "Sagesse", chapters: 19 }, { id: "siracide", name: "Siracide", chapters: 51 },
    { id: "isaie", name: "Isaïe", chapters: 66 }, { id: "jeremie", name: "Jérémie", chapters: 52 }, 
    { id: "lamentations", name: "Lamentations", chapters: 5 }, { id: "baruch", name: "Baruch", chapters: 6 }, 
    { id: "ezechiel", name: "Ézéchiel", chapters: 48 }, { id: "daniel", name: "Daniel", chapters: 14 },
    { id: "osee", name: "Osée", chapters: 14 }, { id: "joel", name: "Joël", chapters: 4 }, 
    { id: "amos", name: "Amos", chapters: 9 }, { id: "abdias", name: "Abdias", chapters: 1 }, 
    { id: "jonas", name: "Jonas", chapters: 4 }, { id: "michee", name: "Michée", chapters: 7 },
    { id: "nahum", name: "Nahum", chapters: 3 }, { id: "habacuc", name: "Habacuc", chapters: 3 }, 
    { id: "sophonie", name: "Sophonie", chapters: 3 }, { id: "aggee", name: "Aggée", chapters: 2 }, 
    { id: "zacharie", name: "Zacharie", chapters: 14 }, { id: "malachie", name: "Malachie", chapters: 3 },
  ],
  nouveau_testament: [
    { id: "matthieu", name: "Matthieu", chapters: 28 }, { id: "marc", name: "Marc", chapters: 16 }, 
    { id: "luc", name: "Luc", chapters: 24 }, { id: "jean", name: "Jean", chapters: 21 }, 
    { id: "actes", name: "Actes", chapters: 28 }, { id: "romains", name: "Romains", chapters: 16 },
    { id: "1corinthiens", name: "1 Corinthiens", chapters: 16 }, { id: "2corinthiens", name: "2 Corinthiens", chapters: 13 },
    { id: "galates", name: "Galates", chapters: 6 }, { id: "ephesiens", name: "Éphésiens", chapters: 6 }, 
    { id: "philippiens", name: "Philippiens", chapters: 4 }, { id: "colossiens", name: "Colossiens", chapters: 4 }, 
    { id: "1thessaloniciens", name: "1 Thessaloniciens", chapters: 5 }, { id: "2thessaloniciens", name: "2 Thessaloniciens", chapters: 3 }, 
    { id: "1timothee", name: "1 Timothée", chapters: 6 }, { id: "2timothee", name: "2 Timothée", chapters: 4 }, 
    { id: "tite", name: "Tite", chapters: 3 }, { id: "philemon", name: "Philémon", chapters: 1 },
    { id: "hebreux", name: "Hébreux", chapters: 13 }, { id: "jacques", name: "Jacques", chapters: 5 }, 
    { id: "1pierre", name: "1 Pierre", chapters: 5 }, { id: "2pierre", name: "2 Pierre", chapters: 3 }, 
    { id: "1jean", name: "1 Jean", chapters: 5 }, { id: "2jean", name: "2 Jean", chapters: 1 },
    { id: "3jean", name: "3 Jean", chapters: 1 }, { id: "jude", name: "Jude", chapters: 1 }, 
    { id: "apocalypse", name: "Apocalypse", chapters: 22 },
  ]
};

const HomePage = () => {
  const [atOpen, setAtOpen] = useState(false);
  const [ntOpen, setNtOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const navigate = useNavigate();

  const handleBookClick = (bookId) => {
    setSelectedBook(selectedBook === bookId ? null : bookId);
  };

  const handleChapterSelect = (bookId, chapter) => {
    navigate(`/bible/${bookId}/${chapter}`);
    setSelectedBook(null);
  };

  const renderBookItem = (book) => (
    <div key={book.id} className="relative">
      <div
        onClick={() => handleBookClick(book.id)}
        className="flex items-center justify-between py-2 px-3 text-sm font-body text-ink-primary hover:text-rubric-red hover:bg-paper rounded transition-colors cursor-pointer group"
      >
        <div className="flex items-center gap-1">
          <ChevronRight className={`h-3 w-3 transition-transform ${selectedBook === book.id ? 'rotate-90 text-rubric-red' : 'opacity-0 group-hover:opacity-100'}`} />
          <span>{book.name}</span>
        </div>
        <span className="text-xs text-ink-secondary">{book.chapters} ch.</span>
      </div>
      
      {selectedBook === book.id && (
        <div className="mt-1 mb-2 ml-4 mr-2">
          <Select onValueChange={(ch) => handleChapterSelect(book.id, ch)}>
            <SelectTrigger className="w-full h-9 text-sm bg-paper border-border">
              <SelectValue placeholder={`Choisir un chapitre (1-${book.chapters})`} />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {Array.from({ length: book.chapters }, (_, i) => i + 1).map((ch) => (
                <SelectItem key={ch} value={ch.toString()} className="text-sm">
                  Chapitre {ch}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="hero-bg min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="text-center max-w-3xl md:max-w-4xl lg:max-w-5xl mx-auto">
          <div className="mb-4">
            <span className="font-decorative text-sm tracking-[0.3em] text-rubric-red uppercase">
              Édition de 1920
            </span>
          </div>
          
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-ink-primary mb-4 leading-tight">
            La Sainte Bible
          </h1>
          
          <div className="ornament my-4">
            <span className="font-decorative text-gold-leaf text-xl">✦</span>
          </div>
          
          <p className="font-body text-ink-secondary mb-2">
            Traduit de la Vulgate par <span className="font-semibold">L.-Cl. Fillion</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <Button 
              asChild 
              size="lg"
              className="bg-ink-primary hover:bg-ink-secondary text-paper font-body px-6 py-5 rounded-sm"
            >
              <Link to="/bible/genese/1">
                <BookOpen className="mr-2 h-5 w-5" strokeWidth={1.5} />
                Commencer la lecture
              </Link>
            </Button>
            
            <Button 
              asChild 
              variant="outline"
              size="lg"
              className="border-ink-primary text-ink-primary hover:bg-paper-dark font-body px-6 py-5 rounded-sm"
            >
              <Link to="/lectures">
                <Calendar className="mr-2 h-5 w-5" strokeWidth={1.5} />
                Lectures du jour
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Table des matières - Collapsible avec sélection de chapitres */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-paper">
        <div className="container mx-auto max-w-4xl md:max-w-5xl lg:max-w-6xl">
          <h2 className="font-heading text-3xl md:text-4xl text-ink-primary mb-2 text-center">
            Table des matières
          </h2>
          <p className="text-center text-sm md:text-base text-ink-secondary mb-8 md:mb-10">
            Cliquez sur un livre pour choisir un chapitre
          </p>

          <div className="space-y-4 md:space-y-6">
            {/* Ancien Testament */}
            <Collapsible open={atOpen} onOpenChange={setAtOpen}>
              <CollapsibleTrigger className="w-full flex items-center justify-between p-4 md:p-5 bg-paper-dark rounded-sm border border-border hover:border-rubric-red transition-colors">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 md:h-6 md:w-6 text-rubric-red" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl md:text-2xl text-ink-primary">
                    Ancien Testament
                  </h3>
                  <span className="text-sm md:text-base text-ink-secondary">({BIBLE_BOOKS.ancien_testament.length} livres)</span>
                </div>
                <ChevronDown className={`h-5 w-5 md:h-6 md:w-6 text-ink-secondary transition-transform ${atOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 p-4 md:p-5 bg-paper-dark rounded-sm">
                  {BIBLE_BOOKS.ancien_testament.map(renderBookItem)}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Nouveau Testament */}
            <Collapsible open={ntOpen} onOpenChange={setNtOpen}>
              <CollapsibleTrigger className="w-full flex items-center justify-between p-4 md:p-5 bg-paper-dark rounded-sm border border-border hover:border-rubric-red transition-colors">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 md:h-6 md:w-6 text-gold-leaf" strokeWidth={1.5} />
                  <h3 className="font-heading text-xl md:text-2xl text-ink-primary">
                    Nouveau Testament
                  </h3>
                  <span className="text-sm md:text-base text-ink-secondary">({BIBLE_BOOKS.nouveau_testament.length} livres)</span>
                </div>
                <ChevronDown className={`h-5 w-5 md:h-6 md:w-6 text-ink-secondary transition-transform ${ntOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 p-4 md:p-5 bg-paper-dark rounded-sm">
                  {BIBLE_BOOKS.nouveau_testament.map(renderBookItem)}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      </section>

      {/* Documents préliminaires */}
      <section className="py-12 px-4 bg-paper-dark">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl text-ink-primary mb-6 text-center">
            Documents préliminaires
          </h2>

          <div className="space-y-3">
            {[
              { path: "/documents/dedicace", title: "Dédicace", desc: "À Monsieur F.-M. Vigouroux" },
              { path: "/documents/avant_propos", title: "Avant-propos", desc: "L'origine et le but de cette œuvre" },
              { path: "/documents/introduction", title: "Introduction", desc: "Ce qu'est la Bible" },
              { path: "/guide-commentaires", title: "Comprendre les commentaires", desc: "Guide de la numérotation, du plan et des abréviations de Fillion" },
              { path: "/documents/mode_emploi", title: "Mode d'emploi", desc: "Comment utiliser cette application" },
            ].map((doc) => (
              <Link
                key={doc.path}
                to={doc.path}
                className="block p-4 bg-paper rounded-sm border border-border hover:border-rubric-red transition-all group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-ink-secondary group-hover:text-rubric-red transition-colors" strokeWidth={1.5} />
                    <div>
                      <h3 className="font-heading text-lg text-ink-primary group-hover:text-rubric-red transition-colors">
                        {doc.title}
                      </h3>
                      <p className="font-body text-sm text-ink-secondary italic">{doc.desc}</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-ink-secondary group-hover:text-rubric-red group-hover:translate-x-1 transition-all" strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Citation */}
      <section className="py-12 px-4 bg-paper">
        <div className="container mx-auto max-w-2xl text-center">
          <blockquote className="font-body text-lg text-ink-primary italic leading-relaxed">
            « La Bible est vraiment le livre de Notre-Seigneur Jésus-Christ. »
          </blockquote>
          <cite className="font-body text-ink-secondary not-italic mt-4 block text-sm">
            — L.-Cl. Fillion
          </cite>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
